import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1704040686428-7534b262d0d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzgzNjk3MTI5fDA&ixlib=rb-4.1.0&q=85";

function Monogram({ name }) {
  const letter = (name || "A").trim()[0] || "A";
  return (
    <div className="w-12 h-12 rounded-full border border-white/60 flex items-center justify-center text-white font-heading text-xl tracking-tight" data-testid="brand-monogram">
      {letter}
    </div>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [brand, setBrand] = useState({ name: "Anvi Signature Spaces", location: "Nagpur, Maharashtra", tagline: "Luxury interior design, engineered." });
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.brand().then((r) => setBrand((b) => ({ ...b, ...r.data }))).catch(() => {});
    document.title = `${brand.name} · Sign in`;
  }, [brand.name]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/home");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-anvi-50" data-testid="login-page">
      {/* Editorial hero — Anvi brand statement */}
      <div className="relative hidden md:block md:w-1/2 min-h-screen">
        <img src={HERO} alt="Anvi interior" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-anvi-900/85 via-anvi-900/60 to-anvi-terracotta/30" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-4" data-testid="brand-header">
            <Monogram name={brand.name} />
            <div>
              <div className="text-[10px] tracking-[0.35em] uppercase text-anvi-100">Interior Firm</div>
              <div className="font-heading text-xl leading-tight" data-testid="brand-name-hero">{brand.name}</div>
              <div className="text-xs text-anvi-100/70">{brand.location}</div>
            </div>
          </div>

          <div className="max-w-md">
            <div className="text-[10px] tracking-[0.35em] uppercase text-anvi-100/70 mb-4">Est. 2018 · Nagpur</div>
            <h1 className="font-heading text-5xl leading-[1.05] mb-6" data-testid="brand-tagline">
              {brand.tagline}
            </h1>
            <p className="text-anvi-100/85 text-sm leading-relaxed">
              From first sketch to final handover — one operating system for the design studio, the site, and the client.
            </p>
          </div>

          <div className="flex items-center gap-8 text-[11px] tracking-[0.15em] uppercase text-anvi-100/70">
            <div>
              <div className="text-white font-medium text-lg tabular-nums">120+</div>
              <div>Homes delivered</div>
            </div>
            <div className="h-8 w-px bg-anvi-100/25" />
            <div>
              <div className="text-white font-medium text-lg tabular-nums">₹65Cr</div>
              <div>Projects executed</div>
            </div>
            <div className="h-8 w-px bg-anvi-100/25" />
            <div>
              <div className="text-white font-medium text-lg tabular-nums">4.9★</div>
              <div>Google rating</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right — Sign-in card */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile-only brand strip */}
          <div className="md:hidden flex items-center gap-3 mb-8 pb-6 border-b border-anvi-200">
            <div className="w-10 h-10 rounded-full bg-anvi-900 text-white flex items-center justify-center font-heading text-lg">{brand.name?.[0] || "A"}</div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-anvi-500">Sign in to</div>
              <div className="font-heading text-lg text-anvi-900">{brand.name}</div>
            </div>
          </div>

          <div className="text-xs tracking-[0.25em] uppercase text-anvi-500 mb-3">Studio Access</div>
          <h2 className="font-heading text-4xl text-anvi-900 mb-2">Welcome back.</h2>
          <p className="text-anvi-500 text-sm mb-8">Sign in to your firm's command center.</p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-2">Email</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input" required autoComplete="email"
                placeholder="you@yourfirm.com"
                className="w-full border border-anvi-200 bg-white rounded-md px-3 py-2.5 focus:outline-none focus:border-anvi-terracotta focus:ring-1 focus:ring-anvi-terracotta transition-colors"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-2">Password</label>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input" required autoComplete="current-password"
                placeholder="••••••••"
                className="w-full border border-anvi-200 bg-white rounded-md px-3 py-2.5 focus:outline-none focus:border-anvi-terracotta focus:ring-1 focus:ring-anvi-terracotta transition-colors"
              />
            </div>
            <button
              type="submit" disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full bg-anvi-terracotta hover:bg-anvi-terracotta-dark text-white rounded-md px-4 py-3 font-medium transition-colors disabled:opacity-60 flex items-center justify-center gap-2 group"
            >
              {submitting ? "Signing in…" : (
                <>
                  Enter studio <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </button>
          </form>

          <div className="mt-10 text-center text-sm text-anvi-500 border-t border-anvi-200 pt-6">
            New firm? <Link to="/signup" className="text-anvi-terracotta hover:underline font-medium" data-testid="signup-link">Create your studio →</Link>
          </div>

          <div className="mt-8 text-[10px] tracking-[0.2em] uppercase text-anvi-400 text-center">
            Powered by InteriorOS · v1.0
          </div>
        </div>
      </div>
    </div>
  );
}
